(() => {
  const VIDEO_SELECTOR = 'video[autoplay]:not([data-pianzi-managed-video="true"]), video[data-pianzi-autoplay="true"]:not([data-pianzi-managed-video="true"])';
  const CONTROL_SELECTOR = [
    '[data-pianzi-video-control]',
    '[data-pianzi-editorial-video-control]',
    '[data-pianzi-horizontal-video-control]',
    '[data-pianzi-cinematic-video-control]',
    '[data-pianzi-split-video-control]',
    '[data-pianzi-carousel-video-control]',
    '.pianzi-mosaic__video-control'
  ].join(',');

  const initialized = new WeakSet();

  function isUserPaused(video) {
    return video.dataset.pianziUserPaused === 'true';
  }

  function prepare(video) {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'auto');

    if (video.hasAttribute('loop') || video.dataset.pianziLoop === 'true') {
      video.loop = true;
      video.setAttribute('loop', '');
    }

    if (video.hasAttribute('autoplay') || video.dataset.pianziAutoplay === 'true') {
      video.autoplay = true;
      video.setAttribute('autoplay', '');
    }
  }

  function attemptPlay(video) {
    if (!video || !video.isConnected || isUserPaused(video)) return;
    if (video.getClientRects().length === 0) return;

    prepare(video);

    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {
        // Muted inline playback is retried after readiness, visibility,
        // pageshow, section reload, or the visitor's first interaction.
      });
    }
  }

  function initializeVideo(video) {
    if (!video || initialized.has(video)) return;
    initialized.add(video);
    prepare(video);

    ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach((eventName) => {
      video.addEventListener(eventName, () => attemptPlay(video), { passive: true });
    });

    attemptPlay(video);
    window.setTimeout(() => attemptPlay(video), 80);
    window.setTimeout(() => attemptPlay(video), 350);
    window.setTimeout(() => attemptPlay(video), 1200);
  }

  function initializeAll(scope = document) {
    if (scope.matches?.(VIDEO_SELECTOR)) initializeVideo(scope);
    scope.querySelectorAll?.(VIDEO_SELECTOR).forEach(initializeVideo);
  }

  function playVisibleVideos() {
    document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      const rect = video.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
      if (visible) attemptPlay(video);
    });
  }

  function markControlledVideos(control) {
    const root = control.closest('.shopify-section, section, [data-section-id]') || document;
    const paused = control.classList.contains('is-paused') || control.getAttribute('aria-pressed') === 'true';

    root.querySelectorAll('video').forEach((video) => {
      video.dataset.pianziUserPaused = paused ? 'true' : 'false';
      if (!paused && video.matches(VIDEO_SELECTOR)) attemptPlay(video);
    });
  }

  function onFirstInteraction() {
    playVisibleVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
  } else {
    initializeAll(document);
  }

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.(CONTROL_SELECTOR);
    if (!control) return;
    window.setTimeout(() => markControlledVideos(control), 0);
  });

  ['pointerdown', 'touchstart', 'keydown'].forEach((eventName) => {
    document.addEventListener(eventName, onFirstInteraction, { once: true, passive: eventName !== 'keydown' });
  });

  window.addEventListener('pageshow', () => {
    initializeAll(document);
    playVisibleVideos();
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) playVisibleVideos();
  });

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:select', (event) => initializeAll(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initializeAll(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
